'use client';

import { useFetchGames, useGameProviderCatalogue } from '@shared/hooks';
import { useCallback, useMemo } from 'react';
import type { GameDetails, VolatilityLevel } from '../../../casino/types/types';
import { getAvailableGameProviders } from '../../../filters/getAvailableGameProviders';
import type { VolatilityFilterValue } from '../../../GamesFilter/GamesFilter';
import type { GamesCategoryFilter } from '../../types/types';
import type { FetchGamesParams } from '../types';

const VOLATILITY_ORDER: readonly VolatilityLevel[] = ['Low', 'Medium', 'High', 'Extreme'] as const;
const VOLATILITY_MAPPING: Record<VolatilityFilterValue, VolatilityLevel> = {
  Low: 'Low',
  Mid: 'Medium',
  High: 'High',
  Extreme: 'Extreme',
} as const;

const getVolatilityIndex = (volatility: VolatilityFilterValue | undefined): number => {
  if (!volatility) return -1;
  const mappedVolatility = VOLATILITY_MAPPING[volatility];
  return VOLATILITY_ORDER.indexOf(mappedVolatility);
};

const getAllowedVolatilities = (min?: string, max?: string): VolatilityLevel[] => {
  const minIndex = min ? getVolatilityIndex(min as VolatilityFilterValue) : 0;
  const maxIndex = max ? getVolatilityIndex(max as VolatilityFilterValue) : VOLATILITY_ORDER.length - 1;

  if (minIndex === -1 || maxIndex === -1) return [...VOLATILITY_ORDER];

  return VOLATILITY_ORDER.slice(minIndex, maxIndex + 1);
};

const sortGames = (games: GameDetails[], order: string): GameDetails[] => {
  const sortingMap: Record<string, (a: GameDetails, b: GameDetails) => number> = {
    asc: (gameA, gameB) => gameA.name.localeCompare(gameB.name),
    desc: (gameA, gameB) => gameB.name.localeCompare(gameA.name),
  };

  const sortFunction = sortingMap[order];
  return sortFunction ? [...games].sort(sortFunction) : games;
};

const matchesVolatilityFilter = (gameVolatilities: string[] | undefined, allowedVolatilities: VolatilityLevel[]): boolean => {
  if (!gameVolatilities?.length) return false;
  return gameVolatilities.some(vol => vol && allowedVolatilities.includes(vol as VolatilityLevel));
};

const matchesProviderFilter = (gameProviders: string[] | undefined, targetProviders: string[]): boolean => {
  if (!gameProviders?.length) return false;
  return gameProviders.some(provider => provider && targetProviders.some(target => provider.toLowerCase().includes(target.toLowerCase())));
};

type UseGameFilteringReturn = {
  filterGames: (params: FetchGamesParams) => GameDetails[];
  availableGameProviders: ReturnType<typeof getAvailableGameProviders>;
  isLoading: boolean;
};

/**
 * Hook to filter and sort games
 * @param filters - GamesCategoryFilter to apply to the games
 * @param isLoggedIn - Whether the user is logged in
 * @returns Object with the filterGames function optimized
 */
function useGameFiltering(filters: GamesCategoryFilter | undefined, isLoggedIn: boolean, isFavourites: boolean, isCasinoProductVertical: boolean): UseGameFilteringReturn {
  const { category, useThirdPartyRecommendations } = filters ?? {};

  // Special case: Casino favourites should exclude live dealer games
  const shouldExcludeLiveDealer = isFavourites && isCasinoProductVertical && !filters;

  const { providers: providerCatalogue } = useGameProviderCatalogue();

  const { games: categoryGames, isLoading } = useFetchGames({
    category,
    useThirdPartyRecommendations,
    isLoggedIn,
    isFavourites,
    isCasinoProductVertical,
    ...(shouldExcludeLiveDealer
      ? {
          categoriesMode: 'exclude' as const,
          excludeCategories: ['Live Dealer'],
        }
      : {}),
  });

  const filterGames = useCallback(
    (params: FetchGamesParams): GameDetails[] => {
      if (categoryGames.length === 0) {
        return [];
      }

      const { volatilityMin, volatilityMax, gameProviders, sorting } = params;

      const targetProviders = gameProviders
        ? gameProviders
            .split(',')
            .map(provider => provider.trim())
            .filter(Boolean)
        : [];

      const allowedVolatilities = getAllowedVolatilities(volatilityMin, volatilityMax);

      const gamesFiltered = categoryGames.filter(game => {
        const hasVolatilityFilter = volatilityMin || volatilityMax;
        const hasProviderFilter = targetProviders.length > 0;

        const passesVolatilityFilter = !hasVolatilityFilter || matchesVolatilityFilter(game.volatility, allowedVolatilities);
        const passesProviderFilter = !hasProviderFilter || matchesProviderFilter(game.provider, targetProviders);

        return passesVolatilityFilter && passesProviderFilter;
      });

      return sorting ? sortGames(gamesFiltered, sorting) : gamesFiltered;
    },
    [categoryGames]
  );

  const categoryGameProvidersKey = useMemo(() => categoryGames.flatMap(game => game.provider ?? []).join('|'), [categoryGames]);

  const providerCatalogueKey = useMemo(() => providerCatalogue.map(provider => provider.value).join(','), [providerCatalogue]);

  const availableGameProviders = useMemo(
    () => getAvailableGameProviders(categoryGames, providerCatalogue),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keys reflect provider content without unstable array identity
    [categoryGameProvidersKey, providerCatalogueKey]
  );

  return { filterGames, availableGameProviders, isLoading };
}

export { useGameFiltering };
export type { UseGameFilteringReturn };
